import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import { ChevronLeft, ChevronRight, ClipboardList, Eye, History, Info, Search, X } from "lucide-react";
import DatePickerField from "../../components/common/DatePickerField";
import { useEmployees } from "../../hooks/useEmployees";
import { formatEmployeeLabel } from "../../components/common/employeeUtils";
import { useToast } from "../Context/ToastContext";
import { fetchPfAdvancedHistory, formatStatutoryError, savePfException, saveVpf } from "./statutoryApi";
import "./pfAdvanced.css";

/* Exception types — keys match the backend (services/pf_advanced_service.py). */
const EXCEPTION_TYPES = [
  {
    value: "pf_exemption",
    label: "PF Exemption",
    help: "This employee is exempt from PF. Normal employee and employer PF will not be calculated from the effective date.",
  },
  {
    value: "employee_higher_wages",
    label: "Employee PF on Higher Wages",
    help: "Employee PF is calculated on approved PF wages above the statutory wage ceiling (₹25,000 from 17 Sep 2026; ₹15,000 before). Employer PF continues as per the organisation's Basic PF settings.",
    example: ["PF Wages = ₹40,000", "Employee PF = 12% × ₹40,000 = ₹4,800", "Employer PF = 12% × ₹25,000 = ₹3,000"],
  },
  {
    value: "employee_employer_higher_wages",
    label: "Employee + Employer PF on Higher Wages",
    help: "Both employee and employer PF are calculated on approved PF wages above the statutory wage ceiling (₹25,000 from 17 Sep 2026; ₹15,000 before).",
    example: ["PF Wages = ₹40,000", "Employee PF = 12% × ₹40,000 = ₹4,800", "Employer PF = 12% × ₹40,000 = ₹4,800"],
  },
];

const EXEMPTION_REASONS = [
  "Excluded employee — PF wages above the statutory wage ceiling at joining",
  "International worker covered under an SSA",
  "Apprentice / trainee under the Apprentices Act",
  "Other (explain in remarks)",
];

const VPF_METHODS = [
  {
    value: "percent_of_pf_wages",
    label: "Percentage of PF Wages",
    help: "Additional employee PF, calculated as this percentage of the employee's PF wages and added to their normal PF. Employer PF does not change.",
  },
];

const EMPTY_EXCEPTION = {
  employee_id: "",
  exception_type: "",
  effective_from: "",
  exemption_reason: "",
  employee_pf_percent: "12",
  employer_pf_percent: "12",
  approval_reference: "",
  remarks: "",
};

/** First day of the current month (YYYY-MM-DD, local time) — default Effective From. */
function firstOfCurrentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

const newException = () => ({ ...EMPTY_EXCEPTION, effective_from: firstOfCurrentMonth() });
const newVpf = () => ({ ...EMPTY_VPF, effective_from: firstOfCurrentMonth() });

/** Reason / Remarks is mandatory only for a PF exemption with the "Other" reason. */
const remarksRequired = (ex) =>
  ex.exception_type === "pf_exemption" && (ex.exemption_reason || "").toLowerCase().startsWith("other");

const EMPTY_VPF = {
  employee_id: "",
  contribution_method: "",
  contribution_value: "",
  effective_from: "",
  remarks: "",
};

const PAGE_SIZE = 10;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 8,
    borderColor: state.isFocused ? "#1e40af" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(30,64,175,0.12)" : "none",
    fontSize: 13,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base) => ({ ...base, fontSize: 13 }),
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(String(iso).length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function EmployeePicker({ id, value, onChange, options, loading, invalid }) {
  const selected = options.find((o) => o.value === value) || null;
  return (
    <Select
      inputId={id}
      options={options}
      value={selected}
      onChange={(opt) => onChange(opt ? opt.value : "")}
      isClearable
      isLoading={loading}
      placeholder="Search by employee name or ID…"
      noOptionsMessage={() => "No matching employee"}
      styles={{
        ...selectStyles,
        control: (base, state) => ({
          ...selectStyles.control(base, state),
          ...(invalid ? { borderColor: "#dc2626" } : {}),
        }),
      }}
      menuPortalTarget={document.body}
    />
  );
}

function FieldLabel({ htmlFor, children, required }) {
  return (
    <label className="pfa-label" htmlFor={htmlFor}>
      {children}
      {required ? <span className="pfa-req">*</span> : null}
    </label>
  );
}

function FieldError({ msg }) {
  return msg ? <p className="pfa-err">{msg}</p> : null;
}

export default function PfAdvancedSettings() {
  const toast = useToast();
  const { employees, loading: employeesLoading } = useEmployees();
  const employeeOptions = useMemo(
    () =>
      (employees || [])
        .map((e) => ({ value: String(e.employee_id), label: formatEmployeeLabel(e) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [employees]
  );

  /* ── Section 1: PF exception ── */
  const [ex, setEx] = useState(newException);
  const [exErrors, setExErrors] = useState({});
  const [exSaving, setExSaving] = useState(false);
  const exType = EXCEPTION_TYPES.find((t) => t.value === ex.exception_type);
  const setExField = (k, v) => {
    setEx((p) => ({ ...p, [k]: v }));
    setExErrors((p) => ({ ...p, [k]: undefined }));
  };

  /* ── Section 2: VPF ── */
  const [vpf, setVpf] = useState(newVpf);
  const [vpfErrors, setVpfErrors] = useState({});
  const [vpfSaving, setVpfSaving] = useState(false);
  const vpfMethod = VPF_METHODS.find((m) => m.value === vpf.contribution_method);
  const setVpfField = (k, v) => {
    setVpf((p) => ({ ...p, [k]: v }));
    setVpfErrors((p) => ({ ...p, [k]: undefined }));
  };

  /* ── Section 3: History ── */
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);

  // Close the history dialog with Escape and stop the page scrolling behind it.
  useEffect(() => {
    if (!viewing) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setViewing(null); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [viewing]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const data = await fetchPfAdvancedHistory({ search, page, pageSize: PAGE_SIZE });
      setHistory({ items: data?.items || [], total: data?.total || 0 });
    } catch (err) {
      setHistoryError(formatStatutoryError(err, "Could not load history"));
    } finally {
      setHistoryLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    const t = setTimeout(loadHistory, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadHistory, search]);

  const validateException = () => {
    const errs = {};
    if (!ex.employee_id) errs.employee_id = "Select an employee.";
    if (!ex.exception_type) errs.exception_type = "Select an exception type.";
    if (!ex.effective_from) errs.effective_from = "Select the effective date.";
    if (remarksRequired(ex) && !ex.remarks.trim()) {
      errs.remarks = "Explain the reason in remarks when the exemption reason is Other.";
    }
    if (ex.exception_type === "pf_exemption" && !ex.exemption_reason) {
      errs.exemption_reason = "Select the exemption reason.";
    }
    const pct = (v) => Number(v) > 0 && Number(v) <= 12;
    if (ex.exception_type && ex.exception_type !== "pf_exemption" && !pct(ex.employee_pf_percent)) {
      errs.employee_pf_percent = "Enter a value above 0 and up to 12.";
    }
    if (ex.exception_type === "employee_employer_higher_wages" && !pct(ex.employer_pf_percent)) {
      errs.employer_pf_percent = "Enter a value above 0 and up to 12.";
    }
    setExErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveException = async () => {
    if (!validateException()) return;
    setExSaving(true);
    try {
      const higher = ex.exception_type !== "pf_exemption";
      const res = await savePfException({
        employee_id: ex.employee_id,
        exception_type: ex.exception_type,
        effective_from: ex.effective_from,
        remarks: ex.remarks.trim() || null,
        approval_reference: ex.approval_reference.trim() || null,
        exemption_reason: ex.exception_type === "pf_exemption" ? ex.exemption_reason : null,
        employee_pf_percent: higher ? Number(ex.employee_pf_percent) : null,
        employer_pf_percent: ex.exception_type === "employee_employer_higher_wages" ? Number(ex.employer_pf_percent) : null,
      });
      toast.success(res?.summary ? `Saved: ${res.summary}` : "PF exception saved");
      setEx(newException());
      setPage(1);
      loadHistory();
    } catch (err) {
      toast.error(formatStatutoryError(err, "Could not save the PF exception"));
    } finally {
      setExSaving(false);
    }
  };

  const validateVpf = () => {
    const errs = {};
    if (!vpf.employee_id) errs.employee_id = "Select an employee.";
    if (!vpf.contribution_method) errs.contribution_method = "Select a contribution method.";
    const v = Number(vpf.contribution_value);
    if (!(v > 0 && v <= 100)) errs.contribution_value = "Enter a value above 0 and up to 100.";
    if (!vpf.effective_from) errs.effective_from = "Select the effective date.";
    setVpfErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveVpf = async () => {
    if (!validateVpf()) return;
    setVpfSaving(true);
    try {
      const res = await saveVpf({
        employee_id: vpf.employee_id,
        contribution_method: vpf.contribution_method,
        contribution_value: Number(vpf.contribution_value),
        effective_from: vpf.effective_from,
        remarks: vpf.remarks.trim() || null,
      });
      toast.success(res?.summary ? `VPF saved: ${res.summary}` : "VPF saved");
      setVpf(newVpf());
      setPage(1);
      loadHistory();
    } catch (err) {
      toast.error(formatStatutoryError(err, "Could not save VPF"));
    } finally {
      setVpfSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(history.total / PAGE_SIZE));
  const firstRow = history.total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const lastRow = Math.min(page * PAGE_SIZE, history.total);

  return (
    <div className="pfa">
      <div className="pfa-intro">
        <Info size={16} aria-hidden />
        <p>
          Advanced settings are for <strong>employee-specific PF exceptions</strong> and{" "}
          <strong>voluntary PF (VPF)</strong>. Employees without an exception or VPF follow the organisation's
          Basic PF settings.
        </p>
      </div>

      {/* ── 1. Employee PF Exception / Override ── */}
      <section className="pfa-card" aria-labelledby="pfa-ex-title">
        <header className="pfa-card-head">
          <span className="pfa-card-icon"><ClipboardList size={18} /></span>
          <div>
            <h3 id="pfa-ex-title">Employee PF Exception / Override</h3>
            <p>Configure an approved PF exception for an individual employee.</p>
          </div>
        </header>

        <div className="pfa-grid pfa-grid--3">
          <div className="pfa-field">
            <FieldLabel htmlFor="pfa-ex-emp" required>Employee</FieldLabel>
            <EmployeePicker
              id="pfa-ex-emp"
              value={ex.employee_id}
              onChange={(v) => setExField("employee_id", v)}
              options={employeeOptions}
              loading={employeesLoading}
              invalid={!!exErrors.employee_id}
            />
            <FieldError msg={exErrors.employee_id} />
          </div>

          <div className="pfa-field">
            <FieldLabel htmlFor="pfa-ex-type" required>Exception Type</FieldLabel>
            <select
              id="pfa-ex-type"
              className={`pfa-input${exErrors.exception_type ? " is-invalid" : ""}`}
              value={ex.exception_type}
              onChange={(e) => setExField("exception_type", e.target.value)}
            >
              <option value="">Select Exception Type</option>
              {EXCEPTION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <FieldError msg={exErrors.exception_type} />
          </div>

          <div className="pfa-field">
            <FieldLabel required>Effective From</FieldLabel>
            <DatePickerField
              hideLabel
              compact
              value={ex.effective_from}
              onChange={(v) => setExField("effective_from", v)}
              placeholder="DD/MM/YYYY"
              error={exErrors.effective_from}
            />
            <FieldError msg={exErrors.effective_from} />
          </div>
        </div>

        <div className="pfa-grid pfa-grid--2">
          <div className="pfa-field">
            <span className="pfa-label">Exception Settings</span>
            <div className="pfa-dynamic">
              {!exType ? (
                <p className="pfa-muted">Fields here change based on the selected exception type.</p>
              ) : (
                <>
                  <p className="pfa-help">{exType.help}</p>

                  {exType.value === "pf_exemption" ? (
                    <div className="pfa-field">
                      <FieldLabel htmlFor="pfa-ex-reason" required>Exemption Reason</FieldLabel>
                      <select
                        id="pfa-ex-reason"
                        className={`pfa-input${exErrors.exemption_reason ? " is-invalid" : ""}`}
                        value={ex.exemption_reason}
                        onChange={(e) => {
                          setExField("exemption_reason", e.target.value);
                          setExErrors((p) => ({ ...p, remarks: undefined }));
                        }}
                      >
                        <option value="">Select reason</option>
                        {EXEMPTION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <FieldError msg={exErrors.exemption_reason} />
                      <p className="pfa-note">No contribution percentage is needed for an exemption.</p>
                    </div>
                  ) : (
                    <>
                      <div className="pfa-field">
                        <span className="pfa-label">PF Wage Treatment</span>
                        <div className="pfa-readonly">Approved Higher / Actual PF Wages</div>
                      </div>
                      <div className="pfa-grid pfa-grid--2 pfa-grid--tight">
                        <div className="pfa-field">
                          <FieldLabel htmlFor="pfa-ex-emp-pct" required>Employee Contribution</FieldLabel>
                          <div className="pfa-suffix">
                            <input
                              id="pfa-ex-emp-pct"
                              type="number"
                              min="0"
                              max="12"
                              step="0.01"
                              className={`pfa-input${exErrors.employee_pf_percent ? " is-invalid" : ""}`}
                              value={ex.employee_pf_percent}
                              onChange={(e) => setExField("employee_pf_percent", e.target.value)}
                            />
                            <span>%</span>
                          </div>
                          <FieldError msg={exErrors.employee_pf_percent} />
                        </div>
                        <div className="pfa-field">
                          {exType.value === "employee_employer_higher_wages" ? (
                            <>
                              <FieldLabel htmlFor="pfa-ex-er-pct" required>Employer Contribution</FieldLabel>
                              <div className="pfa-suffix">
                                <input
                                  id="pfa-ex-er-pct"
                                  type="number"
                                  min="0"
                                  max="12"
                                  step="0.01"
                                  className={`pfa-input${exErrors.employer_pf_percent ? " is-invalid" : ""}`}
                                  value={ex.employer_pf_percent}
                                  onChange={(e) => setExField("employer_pf_percent", e.target.value)}
                                />
                                <span>%</span>
                              </div>
                              <FieldError msg={exErrors.employer_pf_percent} />
                            </>
                          ) : (
                            <>
                              <span className="pfa-label">Employer Contribution</span>
                              <div className="pfa-readonly">Follow Basic Settings</div>
                            </>
                          )}
                        </div>
                      </div>
                      {exType.example ? (
                        <div className="pfa-example">
                          <span>Example</span>
                          {exType.example.map((line) => <div key={line}>{line}</div>)}
                        </div>
                      ) : null}
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          <div>
            <div className="pfa-field">
              <FieldLabel htmlFor="pfa-ex-remarks" required={remarksRequired(ex)}>
                Reason / Remarks
              </FieldLabel>
              <textarea
                id="pfa-ex-remarks"
                rows={4}
                maxLength={1000}
                className={`pfa-input pfa-textarea${exErrors.remarks ? " is-invalid" : ""}`}
                placeholder={remarksRequired(ex) ? "Explain the reason for this exemption…" : "Enter reason or notes (optional)…"}
                value={ex.remarks}
                onChange={(e) => setExField("remarks", e.target.value)}
              />
              <FieldError msg={exErrors.remarks} />
            </div>
            {/* Approval / Reference No. hidden for now (audit-only; existing saved values still show in history).
            <div className="pfa-field">
              <FieldLabel htmlFor="pfa-ex-ref">Approval / Reference No. <span className="pfa-optional">(optional)</span></FieldLabel>
              <input
                id="pfa-ex-ref"
                className="pfa-input"
                maxLength={120}
                placeholder="e.g. HR/PF/2026/014"
                value={ex.approval_reference}
                onChange={(e) => setExField("approval_reference", e.target.value)}
              />
              <p className="pfa-note">
                For audit only: the internal approval note, email or ticket number that authorised this exception.
                It is shown in the history and does not affect PF calculation.
              </p>
            </div>
            */}
          </div>
        </div>

        <div className="pfa-actions">
          <button type="button" className="pfa-btn pfa-btn--ghost" onClick={() => { setEx(newException()); setExErrors({}); }} disabled={exSaving}>
            Clear
          </button>
          <button type="button" className="pfa-btn pfa-btn--primary" onClick={handleSaveException} disabled={exSaving}>
            {exSaving ? "Saving…" : "Save Exception"}
          </button>
        </div>
      </section>

      {/* ── 2. Voluntary PF ── */}
      <section className="pfa-card" aria-labelledby="pfa-vpf-title">
        <header className="pfa-card-head">
          <span className="pfa-card-icon"><ClipboardList size={18} /></span>
          <div>
            <h3 id="pfa-vpf-title">Voluntary PF (VPF)</h3>
            <p>Configure an additional voluntary PF contribution for an individual employee.</p>
          </div>
        </header>

        <div className="pfa-grid pfa-grid--vpf">
          <div className="pfa-field">
            <FieldLabel htmlFor="pfa-vpf-emp" required>Employee</FieldLabel>
            <EmployeePicker
              id="pfa-vpf-emp"
              value={vpf.employee_id}
              onChange={(v) => setVpfField("employee_id", v)}
              options={employeeOptions}
              loading={employeesLoading}
              invalid={!!vpfErrors.employee_id}
            />
            <FieldError msg={vpfErrors.employee_id} />
          </div>
          <div className="pfa-field">
            <FieldLabel htmlFor="pfa-vpf-method" required>Contribution Method</FieldLabel>
            <select
              id="pfa-vpf-method"
              className={`pfa-input${vpfErrors.contribution_method ? " is-invalid" : ""}`}
              value={vpf.contribution_method}
              onChange={(e) => setVpfField("contribution_method", e.target.value)}
            >
              <option value="">Select Contribution Method</option>
              {VPF_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <FieldError msg={vpfErrors.contribution_method} />
          </div>
          <div className="pfa-field">
            <FieldLabel htmlFor="pfa-vpf-value" required>Contribution Value</FieldLabel>
            <div className="pfa-suffix">
              <input
                id="pfa-vpf-value"
                type="number"
                min="0"
                max="100"
                step="0.01"
                className={`pfa-input${vpfErrors.contribution_value ? " is-invalid" : ""}`}
                value={vpf.contribution_value}
                onChange={(e) => setVpfField("contribution_value", e.target.value)}
              />
              <span>%</span>
            </div>
            <FieldError msg={vpfErrors.contribution_value} />
          </div>
          <div className="pfa-field">
            <FieldLabel required>Effective From</FieldLabel>
            <DatePickerField
              hideLabel
              compact
              value={vpf.effective_from}
              onChange={(v) => setVpfField("effective_from", v)}
              placeholder="DD/MM/YYYY"
              error={vpfErrors.effective_from}
            />
            <FieldError msg={vpfErrors.effective_from} />
          </div>
          <div className="pfa-field pfa-field--span2">
            <FieldLabel htmlFor="pfa-vpf-remarks">Reason / Remarks</FieldLabel>
            <textarea
              id="pfa-vpf-remarks"
              rows={2}
              maxLength={1000}
              className="pfa-input pfa-textarea"
              placeholder="Enter reason (optional)…"
              value={vpf.remarks}
              onChange={(e) => setVpfField("remarks", e.target.value)}
            />
          </div>
        </div>

        {vpfMethod ? (
          <div className="pfa-example pfa-example--inline">
            <span>{vpfMethod.label}</span>
            <div>{vpfMethod.help}</div>
            <div>Example (PF wages ₹25,000, VPF 10%): Normal PF ₹3,000 + VPF ₹2,500 = Employee PF ₹5,500 · Employer PF stays ₹3,000</div>
          </div>
        ) : null}

        <div className="pfa-actions">
          <button type="button" className="pfa-btn pfa-btn--ghost" onClick={() => { setVpf(newVpf()); setVpfErrors({}); }} disabled={vpfSaving}>
            Clear
          </button>
          <button type="button" className="pfa-btn pfa-btn--primary" onClick={handleSaveVpf} disabled={vpfSaving}>
            {vpfSaving ? "Saving…" : "Save VPF"}
          </button>
        </div>
      </section>

      {/* ── 3. History (read-only) ── */}
      <section className="pfa-card" aria-labelledby="pfa-hist-title">
        <header className="pfa-card-head pfa-card-head--split">
          <div className="pfa-card-head-main">
            <span className="pfa-card-icon"><History size={18} /></span>
            <div>
              <h3 id="pfa-hist-title">Advanced PF Settings History</h3>
              <p>History of employee PF exceptions and VPF configurations.</p>
            </div>
          </div>
          <div className="pfa-search">
            <Search size={14} aria-hidden />
            <input
              type="search"
              placeholder="Search by employee name or ID…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              aria-label="Search history by employee name or ID"
            />
          </div>
        </header>

        <div className="pfa-table-wrap">
          <table className="pfa-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Employee</th>
                <th>Type</th>
                <th>Details / Setting</th>
                <th>Effective From</th>
                <th>Updated By</th>
                <th>Updated On</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {historyLoading ? (
                <tr><td colSpan={8} className="pfa-table-empty">Loading history…</td></tr>
              ) : historyError ? (
                <tr><td colSpan={8} className="pfa-table-empty pfa-err">{historyError}</td></tr>
              ) : history.items.length === 0 ? (
                <tr><td colSpan={8} className="pfa-table-empty">
                  {search ? "No records match this search." : "No PF exceptions or VPF configured yet."}
                </td></tr>
              ) : (
                history.items.map((r, i) => (
                  <tr key={r.id}>
                    <td>{firstRow + i}</td>
                    <td>{r.employee_code ? `${r.employee_code} - ` : ""}{r.employee_name}</td>
                    <td><span className={`pfa-type pfa-type--${r.type}`}>{r.type_label}</span></td>
                    <td>{r.summary}</td>
                    <td>{fmtDate(r.effective_from)}</td>
                    <td>{r.updated_by || "—"}</td>
                    <td>{fmtDate(r.updated_on)}</td>
                    <td>
                      <button type="button" className="pfa-link" onClick={() => setViewing(r)}>
                        <Eye size={14} /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pfa-pager">
          <span>{history.total ? `Showing ${firstRow} - ${lastRow} of ${history.total} records` : ""}</span>
          <div>
            <button type="button" className="pfa-page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} aria-label="Previous page">
              <ChevronLeft size={14} />
            </button>
            <span className="pfa-page-num">{page}</span>
            <button type="button" className="pfa-page-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} aria-label="Next page">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* Portal to <body> so the dialog opens centred in the viewport, not at the top of a transformed page. */}
      {viewing ? createPortal(
        <div className="pfa-modal-backdrop" onClick={() => setViewing(null)} role="presentation">
          <div className="pfa-modal" role="dialog" aria-modal="true" aria-labelledby="pfa-view-title" onClick={(e) => e.stopPropagation()}>
            <div className="pfa-modal-head">
              <h3 id="pfa-view-title">{viewing.type_label} — {viewing.employee_name}</h3>
              <button type="button" className="pfa-icon-btn" onClick={() => setViewing(null)} aria-label="Close"><X size={16} /></button>
            </div>
            <dl className="pfa-dl">
              <div><dt>Employee</dt><dd>{viewing.employee_code ? `${viewing.employee_code} - ` : ""}{viewing.employee_name}</dd></div>
              <div><dt>Setting</dt><dd>{viewing.summary}</dd></div>
              {viewing.detail?.exemption_reason ? <div><dt>Exemption Reason</dt><dd>{viewing.detail.exemption_reason}</dd></div> : null}
              {viewing.detail?.pf_wage_treatment ? <div><dt>PF Wage Treatment</dt><dd>Approved Higher / Actual PF Wages</dd></div> : null}
              {viewing.detail?.employee_pf_percent != null ? <div><dt>Employee Contribution</dt><dd>{viewing.detail.employee_pf_percent}%</dd></div> : null}
              {viewing.type === "pf_exception" && viewing.detail?.type !== "pf_exemption" ? (
                <div><dt>Employer Contribution</dt><dd>{viewing.detail?.employer_follows_basic_settings ? "Follow Basic Settings" : `${viewing.detail?.employer_pf_percent}%`}</dd></div>
              ) : null}
              {viewing.type === "vpf" ? <div><dt>Contribution</dt><dd>{viewing.detail?.value}% of PF Wages</dd></div> : null}
              <div><dt>Effective From</dt><dd>{fmtDate(viewing.effective_from)}</dd></div>
              {viewing.detail?.approval_reference ? <div><dt>Approval / Reference</dt><dd>{viewing.detail.approval_reference}</dd></div> : null}
              <div><dt>Reason / Remarks</dt><dd>{viewing.remarks || "—"}</dd></div>
              <div><dt>Updated By</dt><dd>{viewing.updated_by || "—"}</dd></div>
              <div><dt>Updated On</dt><dd>{fmtDate(viewing.updated_on)}</dd></div>
            </dl>
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  );
}
